import {
  EuiButton,
  EuiButtonEmpty,
  EuiForm,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from "@inscopix/ideas-eui";
import { useState } from "react";
import { useDataTableContext } from "../store/DataTableProvider";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { FieldTableName } from "./fields/FieldTableName";
import { FieldTableKey } from "./fields/FieldTableKey";
import { ProjectFolderDjango } from "../folders/ProjectFoldersProvider.api";
import { isNonNullish } from "utils/isNonNullish";

type Fields = {
  name: {
    value: string;
    isValid: boolean;
  };
  key: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
};

interface ModalCreateDataTableProps {
  folder?: ProjectFolderDjango | null;
  onClose: () => void;
}

/** Component that renders a modal for create a new data table */
export const ModalCreateDataTable = ({
  folder,
  onClose,
}: ModalCreateDataTableProps) => {
  const createDataTable = useDataTableContext((s) => s.createDataTable);
  const setSelectedTableId = useDataTableContext((s) => s.setSelectedTableId);

  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<Fields>({
    name: {
      value: "",
      isValid: false,
    },
    key: {
      value: "",
      isValid: false,
      isDirty: false,
    },
  });

  const handleFieldChange = <T extends keyof Fields>(
    fieldName: T,
    field: Fields[T],
  ) => {
    setFields((prevFields) => ({
      ...prevFields,
      [fieldName]: field,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const { error, data: tableId } = await createDataTable({
      name: fields.name.value,
      key: fields.key.value,
      folder: isNonNullish(folder) ? folder.id : null,
    });
    setIsLoading(false);

    if (error === undefined) {
      addUtilityToastSuccess("Data table created");
    } else {
      addUtilityToastFailure("Failed to create data table");
    }
    if (tableId !== undefined) {
      setSelectedTableId(tableId);
    }
    onClose();
  };

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Create Data Table</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm>
          <FieldTableName
            autoFocus
            value={fields.name.value}
            onChange={(field) => handleFieldChange("name", field)}
          />
          <FieldTableKey
            tableName={fields.name.value}
            value={fields.key.value}
            onChange={(field) => {
              handleFieldChange("key", { ...field, isDirty: true });
            }}
            autoGenerate={!fields.key.isDirty}
            onAutoGenerate={(key) => {
              handleFieldChange("key", {
                value: key,
                isValid: true,
                isDirty: false,
              });
            }}
          />
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isLoading}
          fill
          onClick={() => void handleSubmit()}
          disabled={Object.values(fields).some((field) => !field.isValid)}
        >
          Create
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
